/**
 * @file PickingQueuePanel.tsx
 * @description Режим «Очередь» экрана заданий на сборку (phase19.14;
 * phase19.23, A1; phase19.31 шаг О4 — макет (г)).
 *
 * Панель отвечает за данные очереди: заголовок с «Обновить» (на телефоне —
 * в общей нижней полосе каркаса, а не плавающая кнопка), полосу «нет сети»,
 * фильтры и список. Список — `ResponsiveList`: таблица от 1024 (Заказ,
 * Состав, Позиций, Склад, На сборке, Статус, «⋯» с «Лист комплектации») и
 * компактный список до 1024 (строка 88, вся — ссылка; печать с телефона не
 * нужна — «⋯» в строках нет).
 */

import { ExternalLink, Printer, Warehouse } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { pickingQueueColumns } from "./picking-columns";
import { queueEmptySpec, queueFilteredSpec } from "./picking-empty";
import type { PickingView } from "./picking-view";
import { PickingPageHeader } from "./PickingPageHeader";
import { PickingQueueFilters } from "./PickingQueueFilters";
import { usePickListPrint } from "./usePickListPrint";
import { usePickingQueue, type QueueTab } from "./usePickingQueue";
import type { FilterChip } from "../../ui/FilterChips";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { OptionSheet } from "../../ui/OptionSheet";
import { ResponsiveList } from "../../ui/ResponsiveList";
import type { RowAction } from "../../ui/RowActionsMenu";
import { BELOW_LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import { useWarehouseMode } from "../../../hooks/useWarehouseMode";
import type { PickingTaskListItemDto } from "../../../lib/api-picking";

interface Props {
  view: PickingView;
  onViewChange: (view: PickingView) => void;
  /** База маршрута WMS: `/shop/:slug/wms`. */
  basePath: string;
  /** Доска заказов — действие пустого состояния. */
  ordersBoardPath: string;
  currentUserId: string | null;
  /** Вкладка при открытии: приходит из `?tab=` (ссылка с обзора склада). */
  initialTab?: QueueTab;
  /** Склад при открытии: `?wh=` из той же ссылки. */
  initialWarehouseId?: string;
}

export function PickingQueuePanel({ view, onViewChange, basePath, ordersBoardPath, currentUserId, initialTab, initialWarehouseId }: Props) {
  const queue = usePickingQueue(currentUserId, initialTab, initialWarehouseId);
  const { printingId, print } = usePickListPrint();
  const navigate = useNavigate();
  const compact = useMediaQuery(BELOW_LG_QUERY);
  const warehouseMode = useWarehouseMode();
  const [warehouseSheet, setWarehouseSheet] = useState(false);
  const columns = useMemo(() => pickingQueueColumns(currentUserId), [currentUserId]);
  const refresh = () => void queue.refresh();

  const chips: FilterChip<QueueTab>[] = [
    { value: "mine", label: "Мои", count: queue.counts.mine },
    { value: "all", label: "Все", count: queue.counts.all },
    // Красный — только когда недостача есть (правило FilterChips).
    { value: "shortage", label: "Недостача", count: queue.counts.shortage, tone: "danger" },
  ];
  const warehouseOptions = [{ value: "", label: "Все склады" }, ...queue.warehouses.map((w) => ({ value: w.id, label: w.name }))];
  const warehouseName = warehouseOptions.find((o) => o.value === queue.warehouseId)?.label ?? "Все склады";

  /** «⋯» строки — только на широком экране: с телефона лист не печатают. */
  const rowActions = (task: PickingTaskListItemDto): RowAction[] => [
    { key: "open", label: "Открыть задание", icon: ExternalLink, run: () => navigate(`${basePath}/picking/${task.id}`) },
    {
      key: "print",
      label: printingId === task.id ? "Готовим лист комплектации…" : "Лист комплектации",
      icon: Printer,
      disabled: printingId === task.id,
      run: () => void print(task),
    },
  ];

  return (
    <>
      <PickingPageHeader
        view={view}
        onViewChange={onViewChange}
        onRefresh={refresh}
        loading={queue.loading}
        loadedAt={queue.loadedAt}
        queueCount={queue.counts.all}
        warehouseAction={
          compact
            ? { key: "warehouse", label: `Склад: ${warehouseName}`, icon: Warehouse, run: () => setWarehouseSheet(true) }
            : undefined
        }
      />

      {queue.offline ? (
        <NoticeStrip tone="warning">
          Нет сети. Показан список{queue.loadedAt ? ` на ${queue.loadedAt.toLocaleTimeString("ru-RU")}` : " из кэша"}.
        </NoticeStrip>
      ) : null}

      <PickingQueueFilters
        chips={chips}
        tab={queue.tab}
        onTabChange={queue.setTab}
        search={queue.search}
        onSearchChange={queue.setSearch}
        warehouses={queue.warehouses}
        warehouseId={queue.warehouseId}
        onWarehouseChange={queue.setWarehouseId}
        loading={queue.loading}
        showWarehouse={!compact}
        autoFocus={warehouseMode}
      />

      <ResponsiveList
        aria-label="Очередь заданий на сборку"
        rows={queue.visible}
        columns={columns}
        rowKey={(task) => task.id}
        rowHref={(task) => `${basePath}/picking/${task.id}`}
        rowLabel={(task) => `Задание по заказу ${task.orderNumber}`}
        rowActions={compact ? undefined : rowActions}
        rowActionsTitle={(task) => `Действия с заданием ${task.number}`}
        rowSize="double"
        compactRowSize="tall"
        tableMinWidth="min-w-[900px]"
        loading={queue.loading}
        error={queue.error}
        onRetry={refresh}
        filtered={!!queue.search}
        empty={queueEmptySpec(queue.tab, () => queue.setTab("all"), ordersBoardPath)}
        filteredEmpty={queueFilteredSpec(() => queue.setSearch(""))}
      />

      <OptionSheet
        open={warehouseSheet}
        onOpenChange={setWarehouseSheet}
        title="Склад"
        value={queue.warehouseId}
        options={warehouseOptions}
        onChange={queue.setWarehouseId}
      />
    </>
  );
}
